import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute as ContextRoute } from '@angular/router';
import { Optional as ContextOptional, ElementRef as ContextElement } from '@angular/core';
import { Subscription as ContextSubscription } from 'rxjs';
import { viewContextKey, viewContextVisible } from '../../../shared/view-context';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../../../services/auth.service';

interface FollowUpItem {
  activityId: number; customerId: number; customerName: string;
  activityType: string; subject: string; followUpOn: string;
}

interface DayCell {
  date: Date; iso: string; inMonth: boolean; isToday: boolean; isPast: boolean;
  items: FollowUpItem[];
}

@Component({
  selector: 'app-crm-follow-ups',
  templateUrl: './follow-ups.component.html',
  styleUrls: ['./follow-ups.component.css'],
})
export class CrmFollowUpsComponent implements OnInit, OnDestroy {
  loading = false;
  viewDate = this.firstOfMonth(new Date());
  today = this.midnight(new Date());
  weeks: DayCell[][] = [];
  selected: DayCell | null = null;
  readonly weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  private loadSubscription?: ContextSubscription;
  private restoredDay: string | null = null;
  private items: FollowUpItem[] = [];
  private readonly destroyed$ = new Subject<void>();

  private viewRegistration?: ContextSubscription;
  private registerViewContext(): void {
    this.viewRegistration = this.viewContext?.registerView({
      key: viewContextKey(this.viewRoute, 'Follow-up calendar'), name: 'Follow-up calendar', priority: -10,
      active: () => viewContextVisible(this.viewElement),
      read: () => ({ month: this.viewDate, selectedDay: this.selected?.iso ?? null }),
      describe: state => JSON.stringify(state),
      restore: state => { this.viewDate = state.month; this.restoredDay = state.selectedDay; this.load(); },
    });
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private snackBar: MatSnackBar,
    public auth: AuthService,
    @ContextOptional() private readonly viewContext?: AppContextService,
    @ContextOptional() private readonly viewRoute?: ContextRoute,
    @ContextOptional() private readonly viewElement?: ContextElement<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.registerViewContext(); this.load(); }
  ngOnDestroy(): void {
    this.viewRegistration?.unsubscribe(); this.loadSubscription?.unsubscribe(); this.destroyed$.next(); this.destroyed$.complete(); }

  get monthLabel(): string {
    return this.viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }

  prevMonth(): void { this.restoredDay = null; this.viewDate = this.addMonths(this.viewDate, -1); this.selected = null; this.load(); }
  nextMonth(): void { this.restoredDay = null; this.viewDate = this.addMonths(this.viewDate, 1); this.selected = null; this.load(); }
  goToday(): void { this.restoredDay = null; this.viewDate = this.firstOfMonth(new Date()); this.selected = null; this.load(); }

  load(): void {
    this.loadSubscription?.unsubscribe();
    this.loading = true;
    const gridStart = this.gridStart();
    const gridEnd = this.addDays(gridStart, 41);
    // Fetch every open follow-up through the last visible day.
    this.loadSubscription = this.http.get<FollowUpItem[]>(`/api/follow-ups?through=${this.iso(gridEnd)}`)
      .pipe(takeUntil(this.destroyed$)).subscribe({
        next: data => { this.items = data; this.buildGrid(); this.loading = false; },
        error: () => { this.snackBar.open('Failed to load follow-ups.', 'OK', { duration: 4000 }); this.loading = false; },
      });
  }

  selectDay(cell: DayCell): void {
    this.restoredDay = null;
    this.selected = cell.items.length ? cell : null;
  }

  complete(item: FollowUpItem): void {
    const note = (prompt('Add a note about how this follow-up was resolved (optional):') ?? '').trim();
    this.http.post(`/api/follow-ups/${item.activityId}/done`, { note: note || null })
      .pipe(takeUntil(this.destroyed$)).subscribe({
        next: () => {
          this.items = this.items.filter(i => i.activityId !== item.activityId);
          this.buildGrid();
          this.snackBar.open('Follow-up completed', undefined, { duration: 2000 });
        },
        error: (e: any) => this.snackBar.open(e?.error?.message ?? 'Failed to complete', 'OK', { duration: 4000 }),
      });
  }

  // A follow-up lives on the customer's interaction timeline, so open that tab directly.
  goToCustomer(id: number): void {
    this.router.navigate(['/customers', id], { queryParams: { tab: 'interaction-tracking' } });
  }

  // ── grid construction ─────────────────────────────────────────────────────
  private buildGrid(): void {
    const byDate = new Map<string, FollowUpItem[]>();
    for (const it of this.items) {
      const key = it.followUpOn.slice(0, 10);
      (byDate.get(key) ?? byDate.set(key, []).get(key)!).push(it);
    }
    const start = this.gridStart();
    const month = this.viewDate.getMonth();
    const weeks: DayCell[][] = [];
    for (let w = 0; w < 6; w++) {
      const row: DayCell[] = [];
      for (let d = 0; d < 7; d++) {
        const date = this.addDays(start, w * 7 + d);
        const iso = this.iso(date);
        row.push({
          date, iso,
          inMonth: date.getMonth() === month,
          isToday: date.getTime() === this.today.getTime(),
          isPast: date.getTime() < this.today.getTime(),
          items: byDate.get(iso) ?? [],
        });
      }
      weeks.push(row);
    }
    this.weeks = weeks;
    if (this.restoredDay) {
      this.selected = weeks.flat().find(cell => cell.iso === this.restoredDay) ?? null;
      this.restoredDay = null;
    }
    // keep the selected day's items in sync after a completion
    if (this.selected) {
      const found = weeks.flat().find(c => c.iso === this.selected!.iso);
      this.selected = found?.items.length ? found : null;
    }
  }

  // ── date helpers ──────────────────────────────────────────────────────────
  private midnight(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
  private firstOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
  private addDays(d: Date, n: number): Date { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }
  private addMonths(d: Date, n: number): Date { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
  private gridStart(): Date { const f = this.firstOfMonth(this.viewDate); return this.addDays(f, -f.getDay()); }
  private iso(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}
